// Patron library panel — book list + page reader + search.
//
// UI states: "catalog" (list of books with progress bars) and "reading"
// (current page with prev/next + search within book). The ClientStore
// holds the state; this panel just reads it and dispatches messages.

import type { ClientStore } from "../state.js";
import type { Net } from "../net.js";
import { openPanel } from "./shared_panel.js";

function renderMarkdown(body: string): string {
  // Minimal, safe-ish markdown: escape HTML first, then apply a small subset.
  const esc = body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .replace(/^### (.*)$/gm, "<h3>$1</h3>")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>")
    .replace(/^# (.*)$/gm, "<h1>$1</h1>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/^---$/gm, "<hr />")
    .split(/\n{2,}/)
    .map((block) => block.startsWith("<h") || block.startsWith("<hr") ? block : `<p>${block.replace(/\n/g, "<br/>")}</p>`)
    .join("\n");
}

export function openLibraryPanel(store: ClientStore, net: Net): void {
  net.send({ t: "library_catalog" });
  openPanel({
    id: "panel-library",
    title: "Patron Library",
    store,
    render(state) {
      const lib = state.library;
      if (lib.locked) {
        return `
          <div class="library-panel locked">
            <h3>Library access is patron-tier only</h3>
            <p class="muted">Become a patron to read the translated corpus (36 historical texts, ~13,500 pages).</p>
            <p class="muted">Reason: <code>${lib.locked}</code></p>
          </div>`;
      }
      if (lib.view === "reading" && lib.page) {
        const p = lib.page;
        const pct = p.book.page_count > 0 ? Math.round((p.page_no / p.book.page_count) * 100) : 0;
        const hitList = lib.searchResults.length
          ? `<ul class="lib-hits">${lib.searchResults.map((h) => `
              <li><button data-jump="${h.page_no}">p.${h.page_no}</button> <span class="muted">${h.page_title}</span> — ${h.snippet}</li>
            `).join("")}</ul>`
          : "";
        return `
          <style>
            .library-panel { font-size:13px; }
            .lib-reader-head { display:flex; justify-content:space-between; align-items:center; gap:8px; margin-bottom:8px; flex-wrap:wrap; }
            .lib-reader-head .meta { font-size:11px; color:var(--muted,#888); }
            .lib-reader-body { max-height:55vh; overflow-y:auto; padding:8px 12px; background:var(--panel-bg-deep,#14161d); border:1px solid var(--panel-line,#2a2f3d); border-radius:4px; line-height:1.55; }
            .lib-reader-body h1 { font-size:16px; margin:6px 0; }
            .lib-reader-body h2 { font-size:14px; margin:6px 0; }
            .lib-reader-body p { margin:6px 0; }
            .lib-reader-nav { display:flex; justify-content:space-between; align-items:center; margin-top:8px; gap:8px; }
            .lib-reader-nav .progress { flex:1; background:#222; height:6px; border-radius:3px; overflow:hidden; }
            .lib-reader-nav .progress > div { height:100%; background:var(--accent,#6b8afd); }
            .lib-search { display:flex; gap:6px; margin:10px 0; }
            .lib-search input { flex:1; }
            .lib-hits { list-style:none; padding:0; margin:0; max-height:140px; overflow-y:auto; }
            .lib-hits li { padding:4px 6px; border-bottom:1px solid var(--panel-line,#2a2f3d); font-size:11px; }
            .lib-hits button { font-size:11px; padding:1px 6px; }
            .library-panel button { font:inherit; padding:4px 10px; min-height:32px; min-width:44px; }
          </style>
          <div class="library-panel">
            <div class="lib-reader-head">
              <div>
                <button id="lib-back">← Catalog</button>
                <strong>${p.book.title}</strong>
                <div class="meta">${p.book.author || "anonymous"}${p.book.year ? " · " + p.book.year : ""}</div>
              </div>
              <div class="meta">Page ${p.page_no} / ${p.book.page_count}</div>
            </div>
            <div class="lib-search">
              <input id="lib-q" placeholder="Search within this book" />
              <button id="lib-search">Search</button>
            </div>
            ${hitList}
            <div class="lib-reader-body">
              ${p.title ? `<h1>${p.title}</h1>` : ""}
              ${renderMarkdown(p.body)}
            </div>
            <div class="lib-reader-nav">
              <button id="lib-prev" ${p.has_prev ? "" : "disabled"}>◀ Prev</button>
              <div class="progress"><div style="width:${pct}%"></div></div>
              <button id="lib-next" ${p.has_next ? "" : "disabled"}>Next ▶</button>
            </div>
          </div>`;
      }
      // Catalog view.
      const rows = lib.books.length
        ? lib.books.map((b) => {
            const pct = b.page_count > 0 ? Math.round((b.last_page / b.page_count) * 100) : 0;
            return `<tr>
              <td><strong>${b.title}</strong><br/><span class="muted">${b.author || "anonymous"}${b.year ? " · " + b.year : ""}</span></td>
              <td><span class="muted">${b.page_count} pp.</span><div class="progress"><div style="width:${pct}%"></div></div></td>
              <td><button data-open="${b.id}">${b.last_page > 0 ? `Resume (p.${b.last_page})` : "Open"}</button></td>
            </tr>`;
          }).join("")
        : `<tr><td colspan="3" class="muted">Catalog is empty — books still ingesting or source directory not mounted.</td></tr>`;
      return `
        <style>
          .library-panel table { width:100%; border-collapse:collapse; font-size:13px; }
          .library-panel th, .library-panel td { text-align:left; padding:6px 8px; border-bottom:1px solid var(--panel-line,#2a2f3d); vertical-align:top; }
          .library-panel .progress { background:#222; height:5px; border-radius:3px; overflow:hidden; margin-top:4px; }
          .library-panel .progress > div { height:100%; background:var(--accent,#6b8afd); }
          .library-panel button { font:inherit; padding:4px 10px; min-height:32px; min-width:44px; }
          .library-panel .lib-corpus-search { display:flex; gap:6px; margin-bottom:10px; }
          .library-panel .lib-corpus-search input { flex:1; }
        </style>
        <div class="library-panel">
          <div class="lib-corpus-search">
            <input id="lib-corpus-q" placeholder="Search across the entire corpus" />
            <button id="lib-corpus-search">Search</button>
          </div>
          ${lib.searchResults.length && lib.view === "catalog" ? `
            <ul class="lib-hits">${lib.searchResults.map((h) => `
              <li><button data-jump-book="${h.book_id}" data-jump="${h.page_no}">${h.book_title} · p.${h.page_no}</button> — ${h.snippet}</li>
            `).join("")}</ul>` : ""}
          <table>
            <thead><tr><th>Title</th><th>Progress</th><th></th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    },
    bind(root) {
      const lib = store.get().library;
      root.querySelectorAll<HTMLButtonElement>("button[data-open]").forEach((b) => {
        b.addEventListener("click", () => {
          const id = Number(b.dataset.open);
          net.send({ t: "library_open", book_id: id });
        });
      });
      root.querySelectorAll<HTMLButtonElement>("button[data-jump]").forEach((b) => {
        b.addEventListener("click", () => {
          const page = Number(b.dataset.jump);
          const bookId = b.dataset.jumpBook ? Number(b.dataset.jumpBook) : lib.page?.book.id;
          if (bookId) net.send({ t: "library_page", book_id: bookId, page_no: page });
        });
      });
      root.querySelector("#lib-back")?.addEventListener("click", () => {
        // Server will respond with library_catalog, which resets view + clears page.
        net.send({ t: "library_catalog" });
      });
      root.querySelector("#lib-prev")?.addEventListener("click", () => {
        const cur = store.get().library;
        if (cur.page && cur.page.has_prev) net.send({ t: "library_page", book_id: cur.page.book.id, page_no: cur.page.page_no - 1 });
      });
      root.querySelector("#lib-next")?.addEventListener("click", () => {
        const cur = store.get().library;
        if (cur.page && cur.page.has_next) net.send({ t: "library_page", book_id: cur.page.book.id, page_no: cur.page.page_no + 1 });
      });
      const bookSearch = root.querySelector<HTMLInputElement>("#lib-q");
      root.querySelector("#lib-search")?.addEventListener("click", () => {
        const q = (bookSearch?.value ?? "").trim();
        if (q.length >= 2 && lib.page) net.send({ t: "library_search", book_id: lib.page.book.id, query: q });
      });
      bookSearch?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          const q = (bookSearch.value ?? "").trim();
          if (q.length >= 2 && lib.page) net.send({ t: "library_search", book_id: lib.page.book.id, query: q });
        }
      });
      const corpusSearch = root.querySelector<HTMLInputElement>("#lib-corpus-q");
      root.querySelector("#lib-corpus-search")?.addEventListener("click", () => {
        const q = (corpusSearch?.value ?? "").trim();
        if (q.length >= 2) net.send({ t: "library_search", query: q });
      });
      corpusSearch?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          const q = (corpusSearch.value ?? "").trim();
          if (q.length >= 2) net.send({ t: "library_search", query: q });
        }
      });
    },
  });
}
